import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Menu, MenuItem, IconButton, Avatar, Button, Chip } from '@mui/material';
import { Link, useRouter } from '@tanstack/react-router';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { getUserInitials, useCurrentUserQuery, UserRole } from 'entities/user';
import { useLogoutMutation } from 'features/auth';
import { Routes } from 'shared/routes';

import { getStyles } from './styles';

interface IHeaderProps {
  isAdminMode?: boolean;
}

const Header = ({ isAdminMode = false }: IHeaderProps) => {
  const { data: currentUser } = useCurrentUserQuery();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const [logout] = useLogoutMutation();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleUserClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    handleUserClose();
    try {
      await logout().unwrap();
      enqueueSnackbar('Logged out successfully', { variant: 'success' });
      router.navigate({ to: Routes.frontend.loginPath });
    } catch {
      enqueueSnackbar('Logout failed. Please try again.', { variant: 'error' });
    }
  };

  const initials = getUserInitials(currentUser);
  const hasAdminAccess = currentUser?.role === UserRole.Admin || currentUser?.role === UserRole.Editor;
  const styles = getStyles(isAdminMode);

  const homeRoute = isAdminMode ? Routes.frontend.adminPath : Routes.frontend.rootPath;

  return (
    <Box component="header" sx={styles.header}>
      <Box sx={styles.left}>
        <Link to={homeRoute} style={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={styles.logo}></Box>
        </Link>
        {isAdminMode && <Chip label="ADMIN PANEL" sx={styles.adminBadge} size="small" />}
        <div id="breadcrumbs-portal" style={{ flex: 1 }} />
      </Box>
      <Box sx={styles.right}>
        {isAdminMode ? (
          <Link to={Routes.frontend.rootPath} style={{ textDecoration: 'none' }}>
            <Button sx={styles.switchButton} startIcon={<ExitToAppIcon sx={{ fontSize: '14px' }} />}>
              Client Side
            </Button>
          </Link>
        ) : (
          hasAdminAccess && (
            <Link to={Routes.frontend.adminPath} style={{ textDecoration: 'none' }}>
              <Button sx={styles.switchButton} startIcon={<AdminPanelSettingsIcon sx={{ fontSize: '14px' }} />}>
                Admin Panel
              </Button>
            </Link>
          )
        )}

        <IconButton onClick={handleUserClick} sx={styles.userIconButton}>
          <Avatar sx={styles.avatar}>{initials || '?'}</Avatar>
        </IconButton>
        <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleUserClose}>
          <MenuItem component={Link} to={Routes.frontend.userProfilePath} onClick={handleUserClose}>
            <PersonOutlineIcon sx={{ marginRight: '8px', fontSize: '20px' }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ marginRight: '8px', fontSize: '20px' }} />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export { Header };
